/**
 * Known-free-font resolution (Sprint 049). Epic 08 lists EVERY used face under
 * "N fonts missing" and demands an agency woff2 upload — even faces freely
 * available from an open webfont provider (e.g. Inter on Google Fonts). This
 * module is the SHARED authority that answers one question, with no DB and no
 * HTTP: is a used `(family, weight, style)` satisfiable from a known-free face,
 * and if so, which bundled asset backs it?
 *
 * It sits in `@bainner/banner-core` on purpose: the exact-match against uploaded
 * `campaign_fonts` is duplicated across three call sites (render/panel resolver
 * `services/api-gateway/src/lib/font-resolution.ts`, the offline export bundler
 * `services/exporter/src/bundle.ts`, and the SPA preview `useCampaignFonts.ts`).
 * A free face must be recognised identically by all three or preview and export
 * diverge ("Inter renders in preview but shows a missing badge in the export").
 * This is the one place each of them consults.
 *
 * Serving strategy (Sprint 049 decision Q2): a matched free face is served from
 * BUNDLED woff2 BYTES, never a live provider CDN URL — the export unit must open
 * offline on any ad server (`services/exporter/src/bundle.ts`). So the resolver
 * yields an `asset` id, not a URL; item ② maps that id to the served bytes. The
 * registry therefore claims ONLY faces whose bytes are (or will be) bundled —
 * registry ⊆ bundled bytes — so `resolveFreeFont` never over-claims a face the
 * pipeline cannot actually paint.
 *
 * Scope mirrors the rest of the font system: capture is weight-only and every
 * face is `'normal'` (italic is OUT per VISION), so a non-normal style never
 * resolves here — it falls through to the upload prompt like any licensed face.
 */
import { collectFontUsage } from './compiler/font-usage.js';
import { SUBSTITUTE_FONT_FAMILY } from './render-constants.js';
/**
 * The known-free registry: common open Google Fonts an agency design is likely
 * to use, each with the weights Google Fonts actually serves. Extending it is a
 * data-only change — add an entry here.
 *
 * TRUTHFULNESS matters: `buildGoogleFontsCssUrl` combines every free face of a
 * design into ONE CSS2 request, so a single weight Google does NOT serve makes
 * the whole request 4xx and drops EVERY free font in that design to the fallback.
 * Therefore this list is CONSERVATIVE — only weights the provider verifiably
 * offers. Under-claiming is safe (the face just shows as "missing" → upload);
 * over-claiming poisons the batch. When unsure about a weight, omit it.
 */
const KNOWN_FREE_FAMILIES = [
    { family: 'Inter', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Montserrat', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Poppins', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Roboto', weights: [100, 300, 400, 500, 700, 900] },
    { family: 'Open Sans', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Lato', weights: [100, 300, 400, 700, 900] },
    { family: 'Raleway', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Work Sans', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Nunito', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Nunito Sans', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Source Sans 3', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Rubik', weights: [300, 400, 500, 600, 700, 800, 900] },
    { family: 'Oswald', weights: [200, 300, 400, 500, 600, 700] },
    { family: 'Merriweather', weights: [300, 400, 700, 900] },
    { family: 'Playfair Display', weights: [400, 500, 600, 700, 800, 900] },
    { family: 'PT Sans', weights: [400, 700] },
    { family: 'Mulish', weights: [200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'DM Sans', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { family: 'Manrope', weights: [200, 300, 400, 500, 600, 700, 800] },
    { family: 'Quicksand', weights: [300, 400, 500, 600, 700] },
];
/** Index by lower-cased family for case-insensitive exact-name lookup. */
const FAMILY_INDEX = new Map(KNOWN_FREE_FAMILIES.map((entry) => [entry.family.toLowerCase(), entry]));
/** Lower-case, collapse internal whitespace to single dashes, strip non-url-safe chars. */
function slug(value) {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * Deterministic bundled-asset filename for a free face. Pure and total (does not
 * consult the registry) so item ② can name the file it bundles the same way the
 * resolver names the one it serves.
 */
export function freeFontAsset(fontFamily, fontWeight, fontStyle = 'normal') {
    return `${slug(fontFamily)}-${fontWeight}-${slug(fontStyle)}.woff2`;
}
/**
 * Resolve a used `(family, weight, style)` to a known-free face, or `null` when
 * it is not satisfiable from the free registry (licensed/unknown family, a
 * weight the provider does not ship, or a non-normal style). Family match is
 * case-insensitive and whitespace-trimmed; weight and style are EXACT — never a
 * closest-weight or a synthesised faux-bold, so the browser is never asked to
 * fake a face we do not actually bundle.
 */
export function resolveFreeFont(fontFamily, fontWeight, fontStyle = 'normal') {
    // Weight-only capture: only 'normal' can resolve; anything else falls through
    // to the upload prompt exactly like a licensed face would.
    if (fontStyle !== 'normal')
        return null;
    const entry = FAMILY_INDEX.get(fontFamily.trim().toLowerCase());
    if (!entry || !entry.weights.includes(fontWeight))
        return null;
    return {
        fontFamily: entry.family,
        fontWeight,
        fontStyle: 'normal',
        asset: freeFontAsset(entry.family, fontWeight, 'normal'),
    };
}
/** Boolean form of {@link resolveFreeFont} for call sites that only need a yes/no. */
export function isFreeFont(fontFamily, fontWeight, fontStyle = 'normal') {
    return resolveFreeFont(fontFamily, fontWeight, fontStyle) !== null;
}
/** Canonical names of every seeded free family (for UI/telemetry/debug). */
export const KNOWN_FREE_FONT_FAMILIES = KNOWN_FREE_FAMILIES.map((e) => e.family);
/**
 * Enumerate every free face the registry claims. Order-stable: families in
 * registry order, weights ascending.
 */
export function allFreeFontFaces() {
    const faces = [];
    for (const entry of KNOWN_FREE_FAMILIES) {
        for (const weight of entry.weights) {
            faces.push({
                fontFamily: entry.family,
                fontWeight: weight,
                fontStyle: 'normal',
                asset: freeFontAsset(entry.family, weight, 'normal'),
            });
        }
    }
    return faces;
}
/** Google Fonts CSS2 endpoint — resolves per-UA `@font-face` blocks over gstatic. */
const GOOGLE_FONTS_CSS2_BASE = 'https://fonts.googleapis.com/css2';
/**
 * Build the Google Fonts CSS2 URL that covers a set of resolved free faces
 * (Sprint 049 decision Q2, revised — pull free faces DIRECTLY from Google Fonts
 * rather than hosting bytes). Live surfaces inject this as a `<link>` and the
 * browser fetches the woff2 from gstatic; the exporter fetches this same URL at
 * bundle time to materialize bytes for an offline ZIP.
 *
 * Weights are grouped per family (first-appearance order), sorted ascending and
 * de-duplicated — the CSS2 `wght@` axis requires ascending values — and family
 * names are `+`-encoded on whitespace (`Open Sans` → `Open+Sans`). Appends
 * `display=swap` to match the `font-display:swap` used everywhere else. Returns
 * `null` for an empty (or all-blank-family) set so callers inject nothing. Style
 * is not encoded: only 'normal' faces ever reach here (italic is out of scope).
 */
export function buildGoogleFontsCssUrl(faces) {
    const order = [];
    const weightsByFamily = new Map();
    for (const face of faces) {
        const family = face.fontFamily.trim();
        if (!family)
            continue;
        let weights = weightsByFamily.get(family);
        if (!weights) {
            weights = new Set();
            weightsByFamily.set(family, weights);
            order.push(family);
        }
        weights.add(face.fontWeight);
    }
    if (order.length === 0)
        return null;
    const clauses = order.map((family) => {
        const weights = [...weightsByFamily.get(family)].sort((a, b) => a - b);
        return `family=${family.replace(/\s+/g, '+')}:wght@${weights.join(';')}`;
    });
    return `${GOOGLE_FONTS_CSS2_BASE}?${clauses.join('&')}&display=swap`;
}
/**
 * Canonical "which known-free faces does this banner need" derivation (Sprint
 * 049) — the single place render/export compile sites compute their free faces,
 * so the Google Fonts `<link>` (live) and the embedded bytes (export) can never
 * disagree about a definition. Pure (no DB): free-ness is a function of the
 * definition's used fonts, so it needs no wire threading through the generator or
 * render messages — each compile site derives it locally from the definition plus
 * the already-resolved uploaded faces.
 *
 * An uploaded face WINS: a `(family, weight)` covered by `uploadedFaces` is never
 * also claimed as free (the agency's licensed/custom upload takes precedence).
 * De-duplicated and order-stable (first appearance), mirroring `collectFontUsage`.
 */
export function freeFacesForDefinition(definition, uploadedFaces = []) {
    const uploaded = new Set(uploadedFaces.map((f) => `${f.fontFamily} ${f.fontWeight}`));
    const seen = new Set();
    const free = [];
    for (const use of collectFontUsage(definition)) {
        const key = `${use.fontFamily} ${use.fontWeight}`;
        if (uploaded.has(key) || seen.has(key))
            continue;
        const face = resolveFreeFont(use.fontFamily, use.fontWeight);
        if (face) {
            seen.add(key);
            free.push(face);
        }
    }
    // THE SUBSTITUTE MUST ACTUALLY LOAD (2026-09-01) — `cssFontFamily` ends every stack
    // with `'Inter', sans-serif`, which is only worth naming if the bytes are fetched.
    // Requested at every weight the design uses, so a 700 headline gets a real 700 rather
    // than a synthesised one. Skipped when the design already uses the substitute itself
    // (the loop above claimed it), and when nothing needs substituting at all.
    const needsSubstitute = collectFontUsage(definition).filter((use) => resolveFreeFont(use.fontFamily, use.fontWeight) === null);
    for (const use of needsSubstitute) {
        const face = resolveFreeFont(SUBSTITUTE_FONT_FAMILY, use.fontWeight);
        if (face === null)
            continue;
        const key = `${face.fontFamily} ${String(face.fontWeight)}`;
        if (uploaded.has(key) || seen.has(key))
            continue;
        seen.add(key);
        free.push(face);
    }
    return free;
}
